import { useControlled } from "@reactuses/core";
import { useCallback, useRef, useState } from "react";
import { Label as RALabel } from "react-aria-components";
import { twMerge } from "tailwind-merge";

import Button from "../Button";
import { useTheme, type Theme } from "../context/theme-context";
import InputErrorMessage from "../InputErrorMessage";
import { m } from "../paraglide/messages";
import CodeMirrorView, { type CodeMirrorViewHandle } from "./CodeMirrorView";
import type { CodeLanguage } from "./lib/code-language";
import {
  formatCode,
  getFormatErrorLines,
  getFormatErrorRanges,
  type CodeFormatErrorRange,
} from "./lib/format-code";

export default function CodeEditor({
  language,
  value,
  onChange,
  defaultValue,
  className,
  label,
  theme: themeProp,
}: {
  readonly defaultValue?: string;
  readonly value?: string;
  readonly onChange: (value: string) => void;
  readonly language: CodeLanguage;
  readonly className?: string;
  readonly label?: string;
  readonly theme?: Theme;
}) {
  const contextTheme = useTheme();
  const theme = themeProp ?? contextTheme;
  const [code, setCode] = useControlled<string>(
    value,
    defaultValue ?? "",
    onChange,
  );
  const [isFormatting, setIsFormatting] = useState(false);
  const [formatError, setFormatError] = useState(false);
  const [formatErrorLines, setFormatErrorLines] = useState<readonly number[]>(
    [],
  );
  const [formatErrorRanges, setFormatErrorRanges] = useState<
    readonly CodeFormatErrorRange[]
  >([]);
  const editorRef = useRef<CodeMirrorViewHandle>(null);

  const applyFormat = useCallback(
    async (current: string) => {
      setIsFormatting(true);
      setFormatError(false);
      setFormatErrorLines([]);
      setFormatErrorRanges([]);
      try {
        const formatted = await formatCode(language, current);
        if (formatted !== current && editorRef.current?.getDoc() === current) {
          setCode(formatted);
        }
      } catch (error) {
        setFormatError(true);
        setFormatErrorLines(getFormatErrorLines(error));
        setFormatErrorRanges(getFormatErrorRanges(error));
      } finally {
        setIsFormatting(false);
      }
    },
    [language, setCode],
  );

  return (
    <div
      className={twMerge(
        "flex h-full flex-col overflow-hidden rounded-lg bg-secondary p-4 text-sm",
        className,
      )}
      data-theme={theme}
    >
      <div className="flex h-8 shrink-0 items-center justify-between">
        {label ? (
          <RALabel className="text-text-tertiary text-sm">{label}</RALabel>
        ) : (
          <span />
        )}
        <Button
          type="button"
          color="primary"
          variant="ghost"
          size="xs"
          isDisabled={isFormatting}
          onPress={() => {
            void applyFormat(editorRef.current?.getDoc() ?? code);
          }}
        >
          {m.format_code()}
        </Button>
      </div>
      <div className="min-h-0 w-full flex-1 font-mono">
        <CodeMirrorView
          ref={editorRef}
          value={code}
          onChange={(nextCode) => {
            setFormatError(false);
            setFormatErrorLines([]);
            setFormatErrorRanges([]);
            setCode(nextCode);
          }}
          language={language}
          theme={theme}
          ariaLabel={label ?? "Code editor"}
          errorLines={formatErrorLines}
          errorRanges={formatErrorRanges}
          onFormat={(source) => {
            void applyFormat(source);
          }}
        />
      </div>
      {formatError && (
        <InputErrorMessage className="mt-2 shrink-0">
          {m.format_code_error()}
        </InputErrorMessage>
      )}
    </div>
  );
}
